import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  FaPrint,
  FaTimes,
  FaFilePdf,
  FaInfoCircle,
  FaCalendarAlt,
  FaChartBar,
  FaFilter,
  FaHistory,
  FaImage,
} from "react-icons/fa";
import PartyCallPrintReport from "./PartyCallPrintReport";
import PartyCallYearlyCalendarReport from "./PartyCallYearlyCalendarReport";
import PartyCallAnnualSummaryReport from "./PartyCallAnnualSummaryReport";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const PrintPreviewModal = ({
  isOpen,
  onClose,
  initialReportType = "yearly-calendar", // "month" | "yearly-calendar" | "annual-statement" | "annual"
  party,
  month,
  year,
  projectId,
  partyProjects,
  calendarWeeks,
  callsForMonth,
  monthStats,
  annualData,
  filteredCallsByProject = [],
  initialIncludeHistory = true,
  initialIncludeImages = true,
}) => {
  const [activeReportType, setActiveReportType] = useState(
    initialReportType || "yearly-calendar"
  );
  const [onlyMonthsWithCalls, setOnlyMonthsWithCalls] = useState(true);
  const [includeHistory, setIncludeHistory] = useState(initialIncludeHistory);
  const [includeImages, setIncludeImages] = useState(initialIncludeImages);
  const [isPrintConfirmOpen, setIsPrintConfirmOpen] = useState(false);

  useEffect(() => {
    if (typeof initialIncludeHistory === "boolean") {
      setIncludeHistory(initialIncludeHistory);
    }
    if (typeof initialIncludeImages === "boolean") {
      setIncludeImages(initialIncludeImages);
    }
  }, [initialIncludeHistory, initialIncludeImages, isOpen]);

  useEffect(() => {
    if (isOpen && initialReportType) {
      if (initialReportType === "annual") {
        setActiveReportType("yearly-calendar");
      } else {
        setActiveReportType(initialReportType);
      }
    }
  }, [isOpen, initialReportType]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose?.();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !party) return null;

  const partyName = party?.CompanyName || "Party";
  const cleanPartyName = partyName.replace(/[^a-zA-Z0-9_-]/g, "_");
  const monthName = MONTH_NAMES[month] || "";

  const documentTitle =
    activeReportType === "month"
      ? `Call_Report_${cleanPartyName}_${monthName}_${year}`
      : activeReportType === "yearly-calendar"
      ? `All_Months_Calendar_${cleanPartyName}_${year}`
      : `Annual_KPI_Summary_${cleanPartyName}_${year}`;

  const handleOpenPrintConfirm = () => {
    setIsPrintConfirmOpen(true);
  };

  const handleDirectPrint = (overrideOptions = null) => {
    setIsPrintConfirmOpen(false);
    if (overrideOptions) {
      if (typeof overrideOptions.includeHistory === "boolean") {
        setIncludeHistory(overrideOptions.includeHistory);
      }
      if (typeof overrideOptions.includeImages === "boolean") {
        setIncludeImages(overrideOptions.includeImages);
      }
    }

    const originalTitle = document.title;
    document.title = documentTitle;

    setTimeout(() => {
      window.print();
    }, 150);

    const restoreTitle = () => {
      document.title = originalTitle;
    };

    window.addEventListener("afterprint", restoreTitle, { once: true });
    setTimeout(restoreTitle, 4000);
  };

  const modalContent = (
    <div
      id="party-call-print-modal-portal"
      className="fixed inset-0 z-[9999] flex flex-col bg-slate-900/90 backdrop-blur-xs select-none"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose?.();
        }
      }}
    >
      {/* Top Controls Toolbar */}
      <div
        id="print-modal-top-toolbar"
        onClick={(e) => e.stopPropagation()}
        className="bg-[#154D75] text-white px-3 sm:px-6 py-2 flex flex-col md:flex-row md:items-center justify-between gap-2 shadow-md shrink-0 border-b border-[#0f3856]"
      >
        {/* Left: Branding & Report Context */}
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center text-white shrink-0 shadow-xs">
            <FaFilePdf className="text-sm" />
          </div>
          <div className="min-w-0">
            <h3 className="text-xs sm:text-sm font-bold text-white tracking-wide">
              {activeReportType === "month"
                ? `Month Report (${monthName} ${year})`
                : activeReportType === "yearly-calendar"
                ? `All Months Calendar & Summary (Pure Year ${year})`
                : `Annual KPI Statement (Year ${year})`}{" "}
              &ndash; {partyName}
            </h3>
            <p className="text-[11px] text-blue-100/80 truncate">
              {activeReportType === "month"
                ? `Period: ${monthName} ${year} • ${callsForMonth.length} Call(s) Logged`
                : activeReportType === "yearly-calendar"
                ? `Pure Year ${year} • 1st Page Index Table + Active Months Calendar (Khali months excluded)`
                : `Entire Year ${year} • ${annualData?.totals?.total || 0} Total Calls in Year`}
            </p>
          </div>
        </div>

        {/* Center: Interactive Report Mode Switcher Tabs */}
        <div className="flex items-center gap-2 flex-wrap self-start md:self-auto shrink-0">
          <div className="inline-flex rounded-lg bg-black/25 p-0.5 border border-white/20 shadow-inner">
            <button
              type="button"
              onClick={() => setActiveReportType("yearly-calendar")}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-bold transition-all cursor-pointer ${
                activeReportType === "yearly-calendar"
                  ? "bg-white text-[#154D75] shadow-xs"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              }`}
              title="All Active Months Calendar & Master Index Sheet"
            >
              <FaCalendarAlt className="text-[10px]" />
              <span>All Months Calendar ({year})</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveReportType("month")}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-bold transition-all cursor-pointer ${
                activeReportType === "month"
                  ? "bg-white text-[#154D75] shadow-xs"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              }`}
              title="Complete Month Calendar + Date-wise Tasks"
            >
              <FaCalendarAlt className="text-[10px]" />
              <span>Month Report ({monthName.slice(0, 3)})</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveReportType("annual-statement")}
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-bold transition-all cursor-pointer ${
                activeReportType === "annual-statement" ||
                activeReportType === "annual"
                  ? "bg-white text-emerald-800 shadow-xs"
                  : "text-white/80 hover:text-white hover:bg-white/10"
              }`}
              title="Full Year 12-Month KPI Summary Breakdown"
            >
              <FaChartBar className="text-[10px]" />
              <span>KPI Statement ({year})</span>
            </button>
          </div>

          {/* Sub-filter for Yearly Calendar: Active Months Only vs All 12 Months */}
          {activeReportType === "yearly-calendar" && (
            <div className="inline-flex rounded-lg bg-black/20 p-0.5 border border-white/15 text-[11px]">
              <button
                type="button"
                onClick={() => setOnlyMonthsWithCalls(true)}
                className={`px-2 py-0.5 rounded font-semibold cursor-pointer transition-colors flex items-center gap-1 ${
                  onlyMonthsWithCalls
                    ? "bg-white/90 text-[#154D75]"
                    : "text-white/80 hover:text-white"
                }`}
                title="Print only months that have recorded calls (Empty months excluded)"
              >
                <FaFilter className="text-[9px]" />
                <span>Active Months with Data</span>
              </button>
              <button
                type="button"
                onClick={() => setOnlyMonthsWithCalls(false)}
                className={`px-2 py-0.5 rounded font-semibold cursor-pointer transition-colors ${
                  !onlyMonthsWithCalls
                    ? "bg-white/90 text-[#154D75]"
                    : "text-white/80 hover:text-white"
                }`}
                title="Print all 12 months (Jan to Dec)"
              >
                All 12 Months
              </button>
            </div>
          )}

          {/* Quick Preferences Toggles: History & Images */}
          <div className="inline-flex rounded-lg bg-black/25 p-0.5 border border-white/20 text-[11px] items-center gap-1">
            <button
              type="button"
              onClick={() => setIncludeHistory((prev) => !prev)}
              className={`px-2 py-0.5 rounded font-semibold cursor-pointer transition-colors flex items-center gap-1 ${
                includeHistory
                  ? "bg-white text-[#154D75] shadow-xs"
                  : "text-white/60 hover:text-white"
              }`}
              title="Toggle Assignment & Forwarding History in Call Details"
            >
              <FaHistory className="text-[9px]" />
              <span>{includeHistory ? "History: With" : "History: Without"}</span>
            </button>
            <button
              type="button"
              onClick={() => setIncludeImages((prev) => !prev)}
              className={`px-2 py-0.5 rounded font-semibold cursor-pointer transition-colors flex items-center gap-1 ${
                includeImages
                  ? "bg-emerald-500 text-white shadow-xs font-bold"
                  : "text-white/60 hover:text-white"
              }`}
              title="Toggle Attached JPG/PNG Photos in Call Details"
            >
              <FaImage className="text-[9px]" />
              <span>{includeImages ? "Images: With" : "Images: Without"}</span>
            </button>
          </div>
        </div>

        {/* Right: Action Buttons */}
        <div className="flex items-center justify-between md:justify-end gap-2 shrink-0">
          <div className="hidden xl:flex items-center gap-1.5 text-xs bg-white/10 px-2.5 py-1 rounded-lg border border-white/20 text-white/90">
            <FaInfoCircle className="text-[11px] text-cyan-300" />
            <span>Select &ldquo;Save as PDF&rdquo; &amp; check &ldquo;Background graphics&rdquo;</span>
          </div>

          <button
            type="button"
            onClick={handleOpenPrintConfirm}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold shadow-xs transition-colors cursor-pointer"
            title="Print or Save as PDF (Choose Options)"
          >
            <FaPrint className="text-xs" />
            <span>Print / Save as PDF</span>
          </button>

          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
            title="Close Preview (Esc)"
          >
            <FaTimes className="text-base" />
          </button>
        </div>
      </div>

      {/* Sheet Preview Scroll Container */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex-1 overflow-y-auto screen-preview-wrapper select-text"
      >
        {activeReportType === "yearly-calendar" ? (
          <PartyCallYearlyCalendarReport
            party={party}
            year={year}
            projectId={projectId}
            partyProjects={partyProjects}
            filteredCallsByProject={filteredCallsByProject}
            onlyMonthsWithCalls={onlyMonthsWithCalls}
            isScreenPreview={true}
          />
        ) : activeReportType === "month" ? (
          <PartyCallPrintReport
            party={party}
            month={month}
            year={year}
            projectId={projectId}
            partyProjects={partyProjects}
            calendarWeeks={calendarWeeks}
            callsForMonth={callsForMonth}
            monthStats={monthStats}
            includeHistory={includeHistory}
            includeImages={includeImages}
            isScreenPreview={true}
          />
        ) : (
          <PartyCallAnnualSummaryReport
            party={party}
            year={year}
            projectId={projectId}
            partyProjects={partyProjects}
            annualData={annualData}
            isScreenPreview={true}
          />
        )}
      </div>

      {/* Confirmation Modal before printing */}
      {isPrintConfirmOpen && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-xs select-none"
          onClick={() => setIsPrintConfirmOpen(false)}
        >
          <div
            className="bg-white text-slate-900 w-full max-w-md rounded-2xl shadow-2xl border border-slate-200 overflow-hidden cursor-default pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-[#154D75] text-white px-5 py-3.5 flex items-center justify-between shadow-md">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center text-white">
                  <FaPrint className="text-xs" />
                </div>
                <div>
                  <h3 className="text-sm font-bold tracking-wide">
                    Choose Print &amp; Export Options
                  </h3>
                  <p className="text-[10px] text-blue-100/80">
                    {cleanPartyName} &bull; Year {year}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsPrintConfirmOpen(false)}
                className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
              >
                <FaTimes className="text-sm" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-3.5">
              <p className="text-xs text-slate-600 font-medium">
                Print shuru karne se pehle choose karein ki report me kya shamil karna hai:
              </p>

              {/* 1. History Selection */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <FaHistory className="text-[#154D75] text-xs" />
                  <h4 className="text-xs font-bold text-slate-900">
                    1. Assignment &amp; Forwarding History
                  </h4>
                </div>
                <p className="text-[10.5px] text-slate-500 mb-2.5 leading-relaxed">
                  Call kis-kis team member ko kab forward hui aur unke remarks shamil karne hain?
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => setIncludeHistory(true)}
                    className={`py-2 px-3 rounded-lg border font-bold cursor-pointer transition-all ${
                      includeHistory
                        ? "bg-[#154D75] text-white border-[#154D75] shadow-xs"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
                    }`}
                  >
                    ✓ With History
                  </button>
                  <button
                    type="button"
                    onClick={() => setIncludeHistory(false)}
                    className={`py-2 px-3 rounded-lg border font-bold cursor-pointer transition-all ${
                      !includeHistory
                        ? "bg-[#154D75] text-white border-[#154D75] shadow-xs"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
                    }`}
                  >
                    ✕ Without History
                  </button>
                </div>
              </div>

              {/* 2. Photo Attachments Selection */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <FaImage className="text-emerald-700 text-xs" />
                  <h4 className="text-xs font-bold text-slate-900">
                    2. Call Photo Attachments (JPG / PNG)
                  </h4>
                </div>
                <p className="text-[10.5px] text-slate-500 mb-2.5 leading-relaxed">
                  Agar call me JPG ya PNG image attachment ho, to use task card par print karna hai?
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => setIncludeImages(true)}
                    className={`py-2 px-3 rounded-lg border font-bold cursor-pointer transition-all ${
                      includeImages
                        ? "bg-emerald-700 text-white border-emerald-700 shadow-xs"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
                    }`}
                  >
                    ✓ With Images (JPG/PNG)
                  </button>
                  <button
                    type="button"
                    onClick={() => setIncludeImages(false)}
                    className={`py-2 px-3 rounded-lg border font-bold cursor-pointer transition-all ${
                      !includeImages
                        ? "bg-slate-700 text-white border-slate-700 shadow-xs"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
                    }`}
                  >
                    ✕ Without Images
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-50 px-5 py-3 border-t border-slate-200 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setIsPrintConfirmOpen(false)}
                className="px-4 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold text-xs transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDirectPrint()}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md transition-all cursor-pointer"
              >
                <FaPrint className="text-xs" />
                <span>Print / Save as PDF Now</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default PrintPreviewModal;
